/*****************************************************************
 *  CUSTOMER BUTLER - ONE CUSTOMER (clean look, Zoho's layout)     *
 *  File: apps/web/src/pages/customer/CustomerRecordPage.js        *
 *  Address: /app/customers/<id>                                   *
 *  What it does: the customer's name, then Zoho's related-list    *
 *  tabs in Zoho's order. "Details" shows every Zoho Contact field *
 *  in Zoho's sections and order, Butler's own fields mixed in     *
 *  where Zoho has them; any field can be changed in place.        *
 *  Reads: the layout door, the customer door, and (Reports tab)   *
 *  the Report Engine's lookup door.                               *
 *  Writes: the customer, through its own door (PUT), which keeps  *
 *  the audit trail; notes and the other tabs through theirs.      *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - LOADING THE CUSTOMER AND THE LAYOUT                *
 *  SECTION 2 - THE TAB BODIES                                     *
 *  SECTION 3 - THE PAGE                                           *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from './customerIcons';
import { PageFrame, QuietLine, Notice } from './customerParts';
import { RecordSections, useSaveOneField } from './RecordSections';
import { TabRow, NotesTab, ReportsTab, NotConnectedTab } from './RecordTabs';
import { PropertiesTab, ActivitiesTab, ContactsTab, ConsentTab, streetWords } from './RelatedLists';
import { fetchLayout, fetchCustomer, saveCustomer } from './customerApi';

/*****************************************************************
 *  SECTION 1 - LOADING THE CUSTOMER AND THE LAYOUT                *
 *****************************************************************/

/* ---- useCustomerRecord(customerId) -----------------------------
   Out: { customer, layout, problemWords, reload, setCustomer }.
   customer null + no problem = still loading.                   */
export function useCustomerRecord(customerId) {
  const [customer, setCustomer] = useState(null);
  const [layout, setLayout] = useState(null);
  const [problemWords, setProblemWords] = useState('');

  const reload = useCallback(async () => {
    try {
      const [loadedCustomer, loadedLayout] = await Promise.all([
        fetchCustomer(customerId),
        fetchLayout('Customer'),
      ]);

      setCustomer(loadedCustomer);
      setLayout(loadedLayout);
      setProblemWords('');
    } catch (loadError) {
      setProblemWords('This customer could not be found, or the connection is down.');
    }
  }, [customerId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { customer, layout, problemWords, reload, setCustomer };
}

/*****************************************************************
 *  SECTION 2 - THE TAB BODIES                                     *
 *****************************************************************/

/* ---- propertyAddresses(customer) -- for the Reports lookup ---- */
export function propertyAddresses(customer) {
  const ownedProperties = customer.properties || [];

  return ownedProperties.map((oneProperty) => oneProperty.addressLine1 || oneProperty.label || streetWords(oneProperty));
}

/* ---- TabBody({ tabKey, tabs, customer, layout, onSaveField, reload }) ---- */
function TabBody({ tabKey, tabs, customer, layout, onSaveField, reload }) {
  const chosenTab = tabs.find((oneTab) => oneTab.key === tabKey) || tabs[0];

  if (!chosenTab.isConnected) {
    return <NotConnectedTab label={chosenTab.label} />;
  }

  if (tabKey === 'details') {
    return <RecordSections record={customer} sections={layout.sections} onSaveField={onSaveField} />;
  }

  if (tabKey === 'notes') {
    return <NotesTab customerId={customer.id} />;
  }

  if (tabKey === 'properties') {
    return <PropertiesTab customerId={customer.id} properties={customer.properties || []} onChanged={reload} />;
  }

  if (tabKey === 'reports') {
    return <ReportsTab email={customer.primaryEmail || ''} addresses={propertyAddresses(customer)} />;
  }

  if (tabKey === 'activities') {
    return <ActivitiesTab customerId={customer.id} />;
  }

  if (tabKey === 'contacts') {
    return <ContactsTab customerId={customer.id} contacts={customer.contacts || []} onChanged={reload} />;
  }

  if (tabKey === 'consent') {
    return <ConsentTab customerId={customer.id} />;
  }

  return <NotConnectedTab label={chosenTab.label} />;
}

/*****************************************************************
 *  SECTION 3 - THE PAGE                                           *
 *****************************************************************/

/* ---- CustomerRecordPage({ onChooseSkin }) ---------------------- */
export function CustomerRecordPage({ onChooseSkin }) {
  const { id: customerId } = useParams();
  const { customer, layout, problemWords, reload, setCustomer } = useCustomerRecord(customerId);
  const [tabKey, setTabKey] = useState('details');

  // WHY (2026-10-15): one copy of the save rule, shared with the HR look.
  const saveOneField = useSaveOneField(saveCustomer, customerId, setCustomer);

  if (problemWords) {
    return (
      <PageFrame onChooseSkin={onChooseSkin}>
        <Link className="cb-back" to="/customers"><Icon name="back" /> Customers</Link>
        <Notice tone="bad" icon="warn">{problemWords}</Notice>
      </PageFrame>
    );
  }

  if (!customer || !layout) {
    return (
      <PageFrame onChooseSkin={onChooseSkin}>
        <QuietLine icon="clock">Loading the customer</QuietLine>
      </PageFrame>
    );
  }

  const reachWords = [customer.primaryPhone, customer.primaryEmail].filter(Boolean).join('   ');
  const tabCounts = {
    properties: (customer.properties || []).length,
    contacts: (customer.contacts || []).length,
  };

  return (
    <PageFrame onChooseSkin={onChooseSkin}>
      <Link className="cb-back" to="/customers"><Icon name="back" /> Customers</Link>
      <header className="cb-head">
        <div>
          <h1 className="cb-title">{customer.displayName || 'Unnamed customer'}</h1>
          {reachWords ? <p className="cb-sub">{reachWords}</p> : null}
        </div>
      </header>

      <TabRow tabs={layout.relatedTabs} chosenKey={tabKey} onChoose={setTabKey} countFor={(oneKey) => tabCounts[oneKey]} />

      <TabBody
        tabKey={tabKey}
        tabs={layout.relatedTabs}
        customer={customer}
        layout={layout}
        onSaveField={saveOneField}
        reload={reload}
      />
    </PageFrame>
  );
}

export default CustomerRecordPage;
